import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';

import useAuthStore from '../../store/authStore';
import useAppStore from '../../store/appStore';
import Slider from '../../components/slider';
import { useLanguage } from '../../context/LanguageContext';
import {
  getTypeIcon,
  getIconBgColor,
  getIconTextColor,
  CheckBadge,
  ArrowLeftIcon,
  DocumentArrowUpIcon,
  MicrophoneIcon,
  SparklesIcon,
  LockClosedIcon,
  CustomDropdown,
} from '../../lib/commonUtils';

const getSubscriptionTier = (subscription) => {
  const planName = subscription?.plan?.toLowerCase() || 'free';

  if (planName.includes('enterprise')) return 'enterprise';
  if (planName.includes('premium')) return 'premium';
  if (planName.includes('pro')) return 'pro';
  if (planName.includes('starter')) return 'starter';
  return 'free';
};

const UpgradePrompt = ({ feature, currentPlan }) => (
  <div className="mt-4 p-6 controlled-glass rounded-2xl border border-yellow-500/20 bg-yellow-500/5 inner-highlight">
    <div className="flex items-start">
      <div className="p-2 bg-yellow-500/10 rounded-lg mr-4">
        <LockClosedIcon className="h-5 w-5 text-yellow-500" />
      </div>
      <div>
        <p className="text-sm font-bold text-white tracking-tight">
          {feature} is restricted
        </p>
        <p className="text-xs font-medium text-white/40 mt-1 italic leading-relaxed">
          This feature is unavailable on your current <span className="text-yellow-500/80 font-bold not-italic capitalize">{currentPlan}</span> plan.
          Upgrade to {feature === 'First Message' ? 'Pro' : 'Starter'} or higher to unlock.
        </p>
        <Link
          to="/billing"
          className="inline-flex items-center mt-4 text-[10px] font-bold uppercase tracking-widest text-yellow-500 hover:text-yellow-400 border-b border-yellow-500/30 hover:border-yellow-500 transition-all pb-0.5"
        >
          Upgrade Plan →
        </Link>
      </div>
    </div>
  </div>
);

const AgentCreate = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { createAgent, currentSubscription } = useAppStore();

  const subscriptionTier = useMemo(() =>
    getSubscriptionTier(currentSubscription),
    [currentSubscription]
  );

  const featuresAvailable = useMemo(() => {
    const tier = subscriptionTier;

    return {
      canEditFirstMessage: tier === 'free' || tier === 'pro' || tier === 'premium' || tier === 'enterprise',
      canUseVoiceConfig: tier === 'free' || tier === 'starter' || tier === 'pro' || tier === 'premium' || tier === 'enterprise',
      canUploadKnowledge: tier === 'free' || tier === 'starter' || tier === 'pro' || tier === 'premium' || tier === 'enterprise',
      canUseAppointmentScheduling: tier === 'free' || tier === 'starter' || tier === 'pro' || tier === 'premium' || tier === 'enterprise',
      canUseAdvancedSettings: tier === 'free' || tier === 'pro' || tier === 'premium' || tier === 'enterprise',
    };
  }, [subscriptionTier]);

  const [selectedType, setSelectedType] = useState('customer_support');
  const [sliderData, setSliderData] = useState({});

  const [voiceFile, setVoiceFile] = useState(null);
  const [knowledgeFile, setKnowledgeFile] = useState(null);

  const voiceInputRef = useRef(null);
  const knowledgeInputRef = useRef(null);

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors, isSubmitting },
    watch,
  } = useForm({
    defaultValues: {
      name: '',
      firstMessage: 'Hello! I am your assistant. How may I help you?',
      user_scenario: '',
      email: user?.email || user?.user_email || '',
      companyName: '',
      phone: '',
      language: '',
      speakingStyle: 'professional',
    },
  });

  const firstMessageValue = watch('firstMessage', '');

  useEffect(() => {
    setValue('email', user?.email || user?.user_email || '');
  }, [user, setValue]);

  const typeCards = useMemo(
    () => ['customer_support', 'lead_generation', 'appointment_scheduling'],
    []
  );

  const handleTypeChange = (type) => {
    if (type === 'appointment_scheduling' && !featuresAvailable.canUseAppointmentScheduling) {
      toast.info('Appointment scheduling requires Starter plan or higher. Please upgrade.');
      return;
    }
    setSelectedType(type);
  };

  const handleSliderDataChange = (data) => {
    setSliderData(data);
  };

  const handleVoiceChange = (e) => {
    if (!featuresAvailable.canUseVoiceConfig) {
      toast.info('Voice configuration requires Starter plan or higher. Please upgrade.');
      return;
    }

    const files = Array.from(e.target.files || []);
    if (files.length > 1) toast.info('Only one voice file is allowed. Using the first one.');
    const file = files[0];
    if (!file) return;

    const isMp3ByExt = /\.mp3$/i.test(file.name);
    const isMp3ByMime = (file.type || '').toLowerCase() === 'audio/mpeg';
    if (!isMp3ByExt && !isMp3ByMime) {
      toast.error('Only MP3 files are allowed for voice.');
      if (voiceInputRef.current) voiceInputRef.current.value = '';
      setVoiceFile(null);
      return;
    }

    const cloned = new File([file.slice(0, file.size, file.type)], file.name, {
      type: file.type || 'audio/mpeg',
      lastModified: Date.now(),
    });
    setVoiceFile(cloned);
  };

  const handleKnowledgeChange = (e) => {
    if (!featuresAvailable.canUploadKnowledge) {
      toast.info('Knowledge base requires Starter plan or higher. Please upgrade.');
      return;
    }

    const files = Array.from(e.target.files || []);
    if (files.length > 1) toast.info('Only one document is allowed. Using the first one.');
    const file = files[0];
    if (!file) return;

    const okExt = /\.(pdf|doc|docx)$/i.test(file.name);
    const mime = (file.type || '').toLowerCase();
    const okMime =
      mime === 'application/pdf' ||
      mime === 'application/msword' ||
      mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

    if (!okExt && !okMime) {
      toast.error('Only PDF or DOC/DOCX files are allowed.');
      if (knowledgeInputRef.current) knowledgeInputRef.current.value = '';
      setKnowledgeFile(null);
      return;
    }

    const cloned = new File([file.slice(0, file.size, file.type)], file.name, {
      type: file.type || 'application/octet-stream',
      lastModified: Date.now(),
    });
    setKnowledgeFile(cloned);
  };

  const clearKnowledgeFile = () => {
    setKnowledgeFile(null);
    if (knowledgeInputRef.current) knowledgeInputRef.current.value = '';
  };

  const clearVoiceFile = () => {
    setVoiceFile(null);
    if (voiceInputRef.current) voiceInputRef.current.value = '';
  };

  const onSubmit = async (data) => {
    if (!selectedType) {
      toast.error('Please select an agent type.');
      return;
    }

    if (selectedType === 'appointment_scheduling' && !featuresAvailable.canUseAppointmentScheduling) {
      toast.error('Appointment scheduling requires Starter plan or higher.');
      return;
    }

    const payload = {
      name: data.name,
      firstMessage: data.firstMessage,
      user_scenario: data.user_scenario,
      email: data.email,
      type: selectedType,
      speakingStyle: data.speakingStyle || undefined,
      businessName: data.companyName || undefined,
      phone: data.phone || undefined,
      language: data.language || undefined,
      knowledgeFiles: knowledgeFile ? [knowledgeFile] : [],
      voiceFile: voiceFile || null,
    };

    if (Object.keys(sliderData).length > 0) {
      payload.calData = sliderData;
    }

    const res = await createAgent(payload);
    if (res?.success) {
      toast.success('Agent created successfully!');
      navigate('/agents');
    } else {
      toast.error(res?.error || 'Failed to create agent');
    }
  };

  return (
    <div className="space-y-8 relative pb-20">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div className="flex items-center">
          <Link
            to="/agents"
            className="mr-6 p-3 bg-white/5 border border-white/10 rounded-2xl text-white/40 hover:text-white hover:bg-white/10 transition-all group"
          >
            <ArrowLeftIcon className="h-6 w-6 group-hover:-translate-x-1 transition-transform" />
          </Link>
          <div>
            <h1 className="text-2xl sm:text-2xl font-bold text-white tracking-tight">Create AI Agent</h1>
            <p className="mt-1 text-sm sm:text-xs font-medium text-white/30 italic">
              Set up your custom voice assistant with a unique personality
            </p>
          </div>
        </div>
        <div className="controlled-glass px-5 py-2.5 rounded-2xl border border-primary-500/20 inner-highlight flex items-center space-x-3 self-start sm:self-center">
          <div className="p-1.5 bg-primary-500/10 rounded-lg">
            <SparklesIcon className="h-4 w-4 text-primary-400" />
          </div>
          <span className="text-[10px] font-bold text-white/50 uppercase tracking-widest leading-none">
            Plan: <span className="text-primary-400 font-bold">{subscriptionTier}</span>
          </span>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-10 sm:space-y-6">
        <div className="controlled-glass p-8 sm:p-6 rounded-[32px] sm:rounded-2xl inner-highlight">
          <div className="flex items-center space-x-4 mb-8 sm:mb-5">
            <div className="p-2.5 sm:p-1.5 bg-white/5 border border-white/10 rounded-xl sm:rounded-lg">
              <SparklesIcon className="h-5 w-5 sm:h-4 w-4 text-primary-400" />
            </div>
            <h3 className="text-xl sm:text-lg font-bold text-white tracking-tight">1. Select Agent Type</h3>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            {typeCards.map((type) => {
              const active = selectedType === type;
              const isAppointmentScheduling = type === 'appointment_scheduling';
              const isDisabled = isAppointmentScheduling && !featuresAvailable.canUseAppointmentScheduling;

              return (
                <button
                  type="button"
                  key={type}
                  disabled={isDisabled}
                  className={`relative p-8 sm:p-5 rounded-[32px] sm:rounded-xl border-2 transition-all flex flex-col items-center text-center group ${active
                    ? 'border-primary-500 bg-primary-500/5 shadow-lg shadow-primary-500/10'
                    : isDisabled
                      ? 'border-white/5 bg-white/[0.02] cursor-not-allowed opacity-40'
                      : 'border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/[0.08]'
                    }`}
                  onClick={() => handleTypeChange(type)}
                >
                  {active && (
                    <div className="absolute -top-3 -right-3">
                      <CheckBadge />
                    </div>
                  )}
                  {isDisabled && (
                    <div className="absolute top-4 right-4 p-1.5 bg-white/10 rounded-lg">
                      <LockClosedIcon className="h-3 w-3 text-white/40" />
                    </div>
                  )}
                  <div className={`p-5 sm:p-3 rounded-2xl sm:rounded-xl mb-6 sm:mb-3 border transition-all ${active
                    ? `${getIconBgColor(type)} border-white/20 shadow-xl`
                    : isDisabled
                      ? 'bg-white/5 border-white/5'
                      : `${getIconBgColor(type)} border-white/5 group-hover:scale-110`
                    }`}>
                    <div className={isDisabled ? 'text-white/20' : getIconTextColor(type)}>
                      <div className="sm:scale-95">
                        {getTypeIcon(type)}
                      </div>
                    </div>
                  </div>
                  <h4 className={`text-base font-bold tracking-tight uppercase ${isDisabled ? 'text-white/20' : 'text-white'}`}>
                    {t(type)}
                  </h4>
                  {isDisabled && (
                    <p className="mt-2 text-[10px] font-bold text-white/20 uppercase tracking-widest italic">
                      Upgrade Required
                    </p>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="controlled-glass p-8 sm:p-6 rounded-[32px] sm:rounded-2xl inner-highlight">
          {featuresAvailable.canUseAppointmentScheduling ? (
            <Slider onDataChange={handleSliderDataChange} />
          ) : (
            <UpgradePrompt
              feature="Appointment Scheduling"
              currentPlan={subscriptionTier}
            />
          )}
        </div>

        <div className="controlled-glass p-8 sm:p-6 rounded-[32px] sm:rounded-2xl inner-highlight">
          <div className="flex items-center space-x-4 mb-8 sm:mb-5">
            <div className="p-2.5 sm:p-1.5 bg-white/5 border border-white/10 rounded-xl sm:rounded-lg">
              <DocumentArrowUpIcon className="h-5 w-5 sm:h-4 w-4 text-blue-400" />
            </div>
            <h3 className="text-xl sm:text-lg font-bold text-white tracking-tight">2. Basic Information</h3>
          </div>

          <div className="grid grid-cols-1 gap-8 sm:gap-5 sm:grid-cols-2">
            <div>
              <label className="block text-[10px] sm:text-[9px] font-bold text-white/40 uppercase tracking-widest mb-3 sm:mb-1.5 ml-1">
                Agent Name <span className="text-red-400 font-bold ml-1">*</span>
              </label>
              <input
                type="text"
                placeholder="Ex: Customer Success Lead"
                className={`input text-base sm:text-sm py-4 sm:py-2.5 ${errors.name ? 'border-red-500/50' : ''}`}
                {...register('name', { required: 'Agent name is required' })}
              />
              {errors.name && <p className="mt-2 text-xs font-bold text-red-400 lowercase italic tracking-tight">{errors.name.message}</p>}
            </div>

            <div>
              <label className="block text-[10px] sm:text-[9px] font-bold text-white/40 uppercase tracking-widest mb-3 sm:mb-1.5 ml-1">Company Name</label>
              <input
                type="text"
                className="input text-base sm:text-sm py-4 sm:py-2.5"
                placeholder="Ex: Apple Inc."
                {...register('companyName')}
              />
            </div>

            <div>
              <label className="block text-[10px] sm:text-[9px] font-bold text-white/40 uppercase tracking-widest mb-3 sm:mb-1.5 ml-1">Support Phone</label>
              <input
                type="tel"
                className="input text-base sm:text-sm py-4 sm:py-2.5"
                placeholder="+1 (555) 000-0000"
                {...register('phone')}
              />
            </div>

            <div>
              <label className="block text-[10px] sm:text-[9px] font-bold text-white/40 uppercase tracking-widest mb-3 sm:mb-1.5 ml-1">
                Admin Email <span className="text-red-400 font-bold ml-1">*</span>
              </label>
              <input
                type="email"
                className="input text-base sm:text-sm py-4 sm:py-2.5 opacity-60 bg-white/5"
                {...register('email', { required: 'Email is required' })}
                readOnly
              />
            </div>
          </div>
        </div>

        <div className="controlled-glass p-8 sm:p-6 rounded-[32px] sm:rounded-2xl inner-highlight">
          <div className="flex items-center space-x-4 mb-8 sm:mb-5">
            <div className="p-2.5 sm:p-1.5 bg-white/5 border border-white/10 rounded-xl sm:rounded-lg">
              <MicrophoneIcon className="h-5 w-5 sm:h-4 w-4 text-emerald-400" />
            </div>
            <h3 className="text-xl sm:text-lg font-bold text-white tracking-tight">3. Voice Configuration</h3>
          </div>

          {featuresAvailable.canUseVoiceConfig ? (
            <div className="space-y-8">
              <div>
                <label className="block text-[10px] sm:text-[9px] font-bold text-white/40 uppercase tracking-widest mb-3 sm:mb-1.5 ml-1">
                  Clone Voice (.mp3)
                </label>
                <div
                  className="relative group cursor-pointer"
                  onClick={() => voiceInputRef.current?.click()}
                >
                  <div className="absolute inset-0 bg-white/[0.02] border-2 border-dashed border-white/10 rounded-[32px] sm:rounded-xl group-hover:bg-white/[0.05] group-hover:border-primary-500/30 transition-all" />
                  <div className="relative py-12 sm:py-8 px-6 text-center space-y-4 sm:space-y-2">
                    <div className="p-4 sm:p-2.5 bg-white/5 rounded-2xl sm:rounded-xl w-fit mx-auto border border-white/10 group-hover:bg-primary-500/10 group-hover:border-primary-500/20 transition-all">
                      <MicrophoneIcon className="h-8 w-8 sm:h-6 w-6 text-white/20 group-hover:text-primary-400" />
                    </div>
                    <div>
                      <p className="text-sm sm:text-xs font-bold text-white">Click to upload or drag & drop</p>
                      <p className="text-[10px] sm:text-[8px] font-bold text-white/20 uppercase tracking-widest mt-1">MP3 only (60+ seconds recommended)</p>
                    </div>
                  </div>
                  <input
                    ref={voiceInputRef}
                    type="file"
                    accept=".mp3"
                    className="sr-only"
                    onChange={handleVoiceChange}
                  />
                </div>

                {voiceFile && (
                  <div className="mt-4 flex items-center justify-between p-4 bg-primary-500/5 border border-primary-500/20 rounded-2xl animate-in fade-in slide-in-from-top-2">
                    <div className="flex items-center space-x-4">
                      <div className="p-2 bg-primary-500/20 rounded-lg">
                        <MicrophoneIcon className="h-4 w-4 text-primary-400" />
                      </div>
                      <span className="text-sm font-bold text-white/80 truncate max-w-xs">{voiceFile.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); clearVoiceFile(); }}
                      className="text-[10px] font-bold uppercase tracking-widest text-red-500 hover:text-red-400 p-2"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-[10px] sm:text-[9px] font-bold text-white/40 uppercase tracking-widest mb-3 sm:mb-1.5 ml-1">Speaking Style</label>
                <Controller
                  name="speakingStyle"
                  control={control}
                  render={({ field }) => (
                    <CustomDropdown
                      value={field.value}
                      onChange={field.onChange}
                      options={[
                        { value: 'professional', label: 'Professional & Polished' },
                        { value: 'friendly', label: 'Warm & Friendly' },
                        { value: 'casual', label: 'Relaxed & Casual' },
                        { value: 'energetic', label: 'High Energy & Exciting' },
                      ]}
                      placeholder="Select Speaking Style"
                    />
                  )}
                />
              </div>
            </div>
          ) : (
            <UpgradePrompt
              feature="Voice Configuration"
              currentPlan={subscriptionTier}
            />
          )}
        </div>

        <div className="controlled-glass p-8 sm:p-6 rounded-[32px] sm:rounded-2xl inner-highlight">
          <div className="flex items-center space-x-4 mb-8 sm:mb-5">
            <div className="p-2.5 sm:p-1.5 bg-white/5 border border-white/10 rounded-xl sm:rounded-lg">
              <DocumentArrowUpIcon className="h-5 w-5 sm:h-4 w-4 text-amber-400" />
            </div>
            <h3 className="text-xl sm:text-lg font-bold text-white tracking-tight">4. Knowledge Base</h3>
          </div>

          {featuresAvailable.canUploadKnowledge ? (
            <div className="space-y-6">
              <div
                className="relative group cursor-pointer"
                onClick={() => knowledgeInputRef.current?.click()}
              >
                <div className="absolute inset-0 bg-white/[0.02] border-2 border-dashed border-white/10 rounded-[32px] sm:rounded-xl group-hover:bg-white/[0.05] group-hover:border-amber-500/30 transition-all" />
                <div className="relative py-12 sm:py-8 px-6 text-center space-y-4 sm:space-y-2">
                  <div className="p-4 sm:p-2.5 bg-white/5 rounded-2xl sm:rounded-xl w-fit mx-auto border border-white/10 group-hover:bg-amber-500/10 group-hover:border-amber-500/20 transition-all">
                    <DocumentArrowUpIcon className="h-8 w-8 sm:h-6 w-6 text-white/20 group-hover:text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm sm:text-xs font-bold text-white">Upload Training PDF / DOCX</p>
                    <p className="text-[10px] sm:text-[8px] font-bold text-white/20 uppercase tracking-widest mt-1">Provide background info for your agent</p>
                  </div>
                </div>
                <input
                  ref={knowledgeInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  className="sr-only"
                  onChange={handleKnowledgeChange}
                />
              </div>

              {knowledgeFile && (
                <div className="flex items-center justify-between p-4 bg-amber-500/5 border border-amber-500/20 rounded-2xl animate-in fade-in slide-in-from-top-2">
                  <div className="flex items-center space-x-4">
                    <div className="p-2 bg-amber-500/20 rounded-lg">
                      <DocumentArrowUpIcon className="h-4 w-4 text-amber-400" />
                    </div>
                    <span className="text-sm font-bold text-white/80 truncate max-w-xs">{knowledgeFile.name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); clearKnowledgeFile(); }}
                    className="text-[10px] font-bold uppercase tracking-widest text-red-500 hover:text-red-400 p-2"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          ) : (
            <UpgradePrompt
              feature="Knowledge Base"
              currentPlan={subscriptionTier}
            />
          )}
        </div>

        <div className="controlled-glass p-8 sm:p-6 rounded-[32px] sm:rounded-2xl inner-highlight">
          <div className="flex items-center space-x-4 mb-2">
            <div className="p-2.5 sm:p-1.5 bg-white/5 border border-white/10 rounded-xl sm:rounded-lg">
              <SparklesIcon className="h-5 w-5 sm:h-4 w-4 text-primary-400" />
            </div>
            <h3 className="text-xl sm:text-lg font-bold text-white tracking-tight">5. AI Behavior</h3>
          </div>

          <div className="mt-10 sm:mt-6 space-y-10 sm:space-y-6">
            {featuresAvailable.canEditFirstMessage ? (
              <div>
                <label className="block text-[10px] sm:text-[9px] font-bold text-white/40 uppercase tracking-widest mb-3 sm:mb-1.5 ml-1">
                  Introduction Phrase <span className="text-red-400 font-bold ml-1">*</span>
                </label>
                <div className="relative">
                  <textarea
                    rows={3}
                    className={`input text-base sm:text-sm py-5 sm:py-3 min-h-[120px] sm:min-h-[100px] ${errors.firstMessage ? 'border-red-500/50' : ''}`}
                    placeholder="Ex: Hello! I'm Valoria, how can I help you today?"
                    {...register('firstMessage', { required: 'First message is required' })}
                  />
                  <div className="absolute bottom-4 right-4 text-[10px] font-bold text-white/20 uppercase tracking-widest">
                    {firstMessageValue.length}/500
                  </div>
                </div>
                {errors.firstMessage && <p className="mt-2 text-xs font-bold text-red-400 lowercase italic tracking-tight">{errors.firstMessage.message}</p>}
              </div>
            ) : (
              <UpgradePrompt feature="First Message" currentPlan={subscriptionTier} />
            )}

            <div>
              <label className="block text-[10px] sm:text-[9px] font-bold text-white/40 uppercase tracking-widest mb-3 sm:mb-1.5 ml-1">
                Mission & Scenario <span className="text-red-400 font-bold ml-1">*</span>
              </label>
              <textarea
                rows={6}
                className={`input text-base sm:text-sm py-5 sm:py-3 min-h-[200px] sm:min-h-[160px] ${errors.user_scenario ? 'border-red-500/50' : ''}`}
                placeholder="Ex: Your mission is to book appointments for a dental clinic. Be polite and ask for their name and preferred time..."
                {...register('user_scenario', { required: 'User Scenario is required' })}
              />
              {errors.user_scenario && <p className="mt-2 text-xs font-bold text-red-400 lowercase italic tracking-tight">{errors.user_scenario.message}</p>}
            </div>
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row justify-end items-center gap-6 sm:gap-4 pt-10 sm:pt-6">
          <Link
            to="/agents"
            className="btn btn-secondary w-full sm:w-auto sm:px-8"
          >
            Cancel Creation
          </Link>
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn btn-primary w-full sm:w-auto sm:px-12"
          >
            <span className="flex items-center justify-center">
              {isSubmitting ? (
                <>
                  <div className="h-4 w-4 border-2 border-white/20 border-t-white rounded-full animate-spin mr-3" />
                  Processing...
                </>
              ) : (
                <>
                  <SparklesIcon className="h-4 w-4" />
                  Initiate Agent
                </>
              )}
            </span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default AgentCreate;